import type { ComponentType } from "react";
import {
  Flex,
  Grid,
  Heading,
  Icon,
  Image,
  Text,
  type TextProps,
} from "@chakra-ui/react";
import agencyLogoPlaceholder from "assets/agency-logo-placeholder.png";
import EmailIcon from "assets/icons/medium/email.svg?react";
import MapPinIcon from "assets/icons/medium/map-pin.svg?react";
import PhoneIcon from "assets/icons/medium/phone.svg?react";
import { PageCard } from "components/page-card";
import type { Agency } from "types/contact";

type AgencyDetailsProps = {
  agency?: Agency;
};

function IconRow({
  icon,
  children,
}: {
  icon: ComponentType;
  children: React.ReactNode;
}) {
  return (
    <Flex align={"center"} gap={1}>
      <Icon as={icon} boxSize={"12px"} mr={1} />
      {children}
    </Flex>
  );
}

function AgencyDetailsText({ children, ...rest }: TextProps) {
  return (
    <Text fontSize={{ base: "md", lg: "lg" }} color={"gray.600"} {...rest}>
      {children}
    </Text>
  );
}

export function AgencyDetails({ agency }: AgencyDetailsProps) {
  return (
    <PageCard heading={"Agency details"}>
        <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={"6"}>
        <Flex align={"center"} gap={8}>
          <Image
            src={agency?.display_picture ?? agencyLogoPlaceholder}
            h={{ base: "40px", lg: "50px" }}
          />
          <Flex direction={"column"} gap={2}>
            <Heading size={"md"}>{agency?.display_name}</Heading>
            <IconRow icon={MapPinIcon}>
              <AgencyDetailsText>
                {agency?.primary_address ?? "-"}
              </AgencyDetailsText>
            </IconRow>
          </Flex>
        </Flex>

        <Flex direction={"column"} gap={2}>
          <Heading size={"md"}>Contact</Heading>
          <IconRow icon={PhoneIcon}>
            <AgencyDetailsText>
              {agency?.primary_phone ? (
                <a href={`tel:${agency.primary_phone}`}>
                  {agency.primary_phone}
                </a>
              ) : (
                "-"
              )}
            </AgencyDetailsText>
          </IconRow>
          <IconRow icon={EmailIcon}>
            <AgencyDetailsText
              wordBreak={"break-all"}
              overflowWrap={"break-word"}
            >
              {agency?.primary_email ? (
                <a href={`mailto:${agency.primary_email}`}>
                  {agency.primary_email}
                </a>
              ) : (
                "-"
              )}
            </AgencyDetailsText>
          </IconRow>
        </Flex>
      </Grid>
    </PageCard>
  );
}
